/**
 * Shared helpers for rating and other features (star icon, confetti, slide visibility).
 * Mirrors plugin/lectura helpers.js for parity.
 */

export function starIcon(filled: boolean): SVGSVGElement {
  const path = filled
    ? 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z'
    : 'M12 5.5l1.2 2.43 2.68.39-1.94 1.89.46 2.68L12 11.5l-2.4 1.26.46-2.68-1.94-1.89 2.68-.39L12 5.5M2 9.27l6.91 1.01L12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27z';
  const s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  s.setAttribute('viewBox', '0 0 24 24');
  s.setAttribute('fill', 'currentColor');
  const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  p.setAttribute('d', path);
  s.appendChild(p);
  return s;
}

/**
 * Fire confetti if global confetti() is available (e.g. canvas-confetti).
 */
let confettiLoadPromise: Promise<void> | null = null;

function loadConfettiScript(): Promise<void> {
  if (typeof confetti === 'function') return Promise.resolve();
  if (confettiLoadPromise) return confettiLoadPromise;
  confettiLoadPromise = new Promise<void>((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('No document available for confetti script load.'));
      return;
    }
    const existing = document.querySelector('script[data-lectura-confetti="1"]') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load confetti script.')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js';
    script.async = true;
    script.defer = true;
    script.setAttribute('data-lectura-confetti', '1');
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Failed to load confetti script.')), { once: true });
    document.head.appendChild(script);
  })
    .catch(() => {
      confettiLoadPromise = null;
      throw new Error('Could not initialize confetti.');
    });
  return confettiLoadPromise ?? Promise.reject(new Error('Could not initialize confetti.'));
}

export function fireConfetti(): void {
  const fire = (): void => {
    if (typeof confetti !== 'function') return;
    confetti({
      particleCount: 80,
      spread: 60,
      origin: { y: 0.85 },
      colors: ['#7c3aed', '#a78bfa', '#fbbf24', '#34d399', '#fff']
    });
  };
  if (typeof confetti === 'function') {
    fire();
    return;
  }
  loadConfettiScript().then(fire).catch(() => {});
}

export interface RatingVisibilityConfig {
  showRatingAfterSlide?: 'last' | number;
}

/**
 * Whether the rating pill should be visible for the current slide.
 */
export function isRatingSlideVisible(
  config: RatingVisibilityConfig | null | undefined,
  getSlideIndex: () => number,
  getTotalSlides: () => number
): boolean {
  const total = getTotalSlides();
  if (total === 0) return false;
  const current = getSlideIndex();
  const target = config?.showRatingAfterSlide;
  if (target === 'last') return current === total - 1;
  if (typeof target === 'number') return current >= target;
  return current === total - 1;
}
